import * as React from 'react'
import { useState } from 'react'
import { createPortal } from 'react-dom'
import axios from 'axios'
import { withTranslations, csrfToken } from 'helpers'
import { useTranslation } from 'hooks'
import Modal from '../../shared/Modal/Modal'
import ProviderStep, { Provider } from './ProviderStep'
import UploadStep from './UploadStep'
import ConfirmStep from './ConfirmStep'
import * as S from './ConsignmentRetrieval.styles'

type Step = 'provider' | 'upload' | 'confirm'

const STEPS: Step[] = ['provider', 'upload', 'confirm']

interface RetrievalModalProps {
  userId: number
  saleCollectionId: number
  productTitle: string
  productImageUrl: string
  size: string
  styleCode: string
  buttonLabel?: string
}

const RetrievalModal: React.FC<RetrievalModalProps> = ({
  userId,
  saleCollectionId,
  productTitle,
  productImageUrl,
  size,
  styleCode,
  buttonLabel
}) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [step, setStep] = useState<Step>('provider')
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null)
  const [file, setFile] = useState<File | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const open = () => setIsOpen(true)
  const close = () => {
    setIsOpen(false)
    setTimeout(() => {
      setStep('provider')
      setSelectedProvider(null)
      setFile(null)
      setSubmitError(null)
    }, 200)
  }

  const handleSubmit = async () => {
    if (!selectedProvider || !file) return
    setIsSubmitting(true)
    setSubmitError(null)

    try {
      const formData = new FormData()
      formData.append('sale_collection_id', String(saleCollectionId))
      formData.append('provider', selectedProvider.key)
      formData.append('label_pdf', file)
      formData.append('authenticity_token', csrfToken)

      await axios.post(
        `/api/v1/users/${userId}/consignment_retrievals`,
        formData
      )

      close()
      window.location.reload()
    } catch (err: any) {
      const message =
        err.response?.data?.error ||
        t('views.account.consignment_retrieval.submit_error')
      setSubmitError(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  const canAdvanceToUpload = selectedProvider !== null
  const canAdvanceToConfirm = file !== null

  const stepIndex = STEPS.indexOf(step)

  return (
    <>
      <S.RetrievalActions>
        <S.RetrievalActionBtn role="button" onClick={open}>
          {buttonLabel || t('views.account.consignment_retrieval.retrieve_button')}
        </S.RetrievalActionBtn>
      </S.RetrievalActions>

      {isOpen &&
        createPortal(
          <Modal toggleModal={close} drawer>
            <S.ModalContainer>
              <S.ModalHeader>
                <S.Title>
                  {t('views.account.consignment_retrieval.modal_title')}
                </S.Title>
                <S.Steps>
                  {[
                    t('views.account.consignment_retrieval.step_provider'),
                    t('views.account.consignment_retrieval.step_upload'),
                    t('views.account.consignment_retrieval.step_confirm')
                  ].map((label, i) => (
                    <S.StepLabel
                      key={label}
                      active={i === stepIndex}
                      done={i < stepIndex}
                    >
                      {label}
                    </S.StepLabel>
                  ))}
                </S.Steps>
              </S.ModalHeader>

              <S.Body>
                <S.ProductCard>
                  <S.ProductImage src={productImageUrl} alt={productTitle} />
                  <S.ProductInfo>
                    <S.ProductTitle>{productTitle}</S.ProductTitle>
                    <S.ProductMeta>
                      {styleCode} &middot; {size}
                    </S.ProductMeta>
                  </S.ProductInfo>
                </S.ProductCard>

                {step === 'provider' && (
                  <ProviderStep
                    selected={selectedProvider?.key ?? null}
                    onSelect={setSelectedProvider}
                  />
                )}

                {step === 'upload' && (
                  <UploadStep file={file} onFileSelect={setFile} />
                )}

                {step === 'confirm' && selectedProvider && file && (
                  <ConfirmStep
                    provider={selectedProvider}
                    fileName={file.name}
                    productTitle={productTitle}
                    size={size}
                    submitError={submitError}
                  />
                )}
              </S.Body>

              <S.Footer>
                {step === 'provider' && (
                  <>
                    <S.SecondaryBtn type="button" onClick={close}>
                      {t('shared.cancel')}
                    </S.SecondaryBtn>
                    <S.PrimaryBtn
                      type="button"
                      disabled={!canAdvanceToUpload}
                      onClick={() => setStep('upload')}
                    >
                      {t('shared.continue')}
                    </S.PrimaryBtn>
                  </>
                )}

                {step === 'upload' && (
                  <>
                    <S.SecondaryBtn
                      type="button"
                      onClick={() => setStep('provider')}
                    >
                      {t('shared.back')}
                    </S.SecondaryBtn>
                    <S.PrimaryBtn
                      type="button"
                      disabled={!canAdvanceToConfirm}
                      onClick={() => setStep('confirm')}
                    >
                      {t('shared.continue')}
                    </S.PrimaryBtn>
                  </>
                )}

                {step === 'confirm' && selectedProvider && (
                  <>
                    <S.SecondaryBtn
                      type="button"
                      onClick={() => setStep('upload')}
                    >
                      {t('shared.back')}
                    </S.SecondaryBtn>
                    <S.PrimaryBtn
                      type="button"
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                    >
                      {isSubmitting
                        ? t('shared.loading')
                        : t(
                            'views.account.consignment_retrieval.confirm_and_pay',
                            { fee: selectedProvider.fee }
                          )}
                    </S.PrimaryBtn>
                  </>
                )}
              </S.Footer>
            </S.ModalContainer>
          </Modal>,
          document.body
        )}
    </>
  )
}

export default withTranslations(RetrievalModal)
